import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Switch } from "@/components/ui/switch";

const SizeForm = ({ settings, refresh }) => {
  const [usdVal, setUsd] = useState(String(settings.trade_usd));
  const [unlimited, setUnlimited] = useState(!settings.trades_per_day);
  const [perDay, setPerDay] = useState(String(settings.trades_per_day || 5));
  const target = unlimited ? 0 : parseInt(perDay, 10);
  const dirty = parseFloat(usdVal) !== settings.trade_usd || target !== (settings.trades_per_day || 0);
  const save = async () => {
    try {
      await api.post("/settings", { trade_usd: parseFloat(usdVal), trades_per_day: target });
      toast.success("Trade settings updated");
      refresh();
    } catch (e) {
      toast.error(errMsg(e, "Update failed"));
    }
  };
  const box = "flex items-center border border-[#1E293B] bg-[#07090D] focus-within:border-[#14F195]/60 transition-colors";
  return (
    <div className="mt-4 space-y-2" data-testid="size-form">
      <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
        <label className="text-[10px] text-slate-400">$ per trade
          <div className={box}><span className="pl-2 text-slate-500 font-mono text-xs">$</span>
            <input data-testid="trade-usd-input" type="number" min="0.1" step="0.1" value={usdVal} onChange={(e) => setUsd(e.target.value)}
              className="w-full bg-transparent px-2 py-1.5 font-mono text-sm text-white outline-none" /></div>
        </label>
        <label className="text-[10px] text-slate-400">Trades / day
          <div className={`${box} ${unlimited ? "opacity-40" : ""}`}>
            <input data-testid="trades-per-day-input" type="number" min="1" max="1000" value={unlimited ? "" : perDay} placeholder="∞"
              disabled={unlimited} onChange={(e) => setPerDay(e.target.value)}
              className="w-full bg-transparent px-2 py-1.5 font-mono text-sm text-white outline-none" /></div>
        </label>
        <button data-testid="size-save-btn" onClick={save} disabled={!dirty}
          className="px-3 py-1.5 bg-[#14F195] text-[#07090D] text-xs font-bold hover:bg-[#0fd884] disabled:opacity-30 transition-colors">Save</button>
      </div>
      <label className="flex items-center justify-between text-xs">
        <span className="text-slate-300">Unlimited trades (24/7 market)</span>
        <Switch data-testid="unlimited-trades-toggle" checked={unlimited} onCheckedChange={setUnlimited} />
      </label>
    </div>
  );
};

export const RulesCard = ({ settings, refresh }) => {
  const rows = [
    ["Router", "Raydium · 3% slippage, pump.fun fallback · 10% (sells widen to 20%)"],
    ["Filters", settings.trades_per_day ? "liq ≥ $50 · 1 copy / trader / day" : "liq ≥ $50 · 1 open position / token"],
  ];
  return (
    <div className="panel p-5 rise" style={{ animationDelay: "80ms" }} data-testid="rules-card">
      <div className="panel-title">Execution rules</div>
      <div className="mt-4 border border-[#14F195]/40 bg-[#14F195]/10 p-3" data-testid="exit-rules">
        <div className="text-[10px] font-mono tracking-widest text-[#14F195]/80">EXIT RULE</div>
        <div className="mt-1 font-mono text-lg font-bold text-[#14F195]" data-testid="exit-rule-value">Mirror the trader's sell</div>
        <div className="text-[10px] text-slate-400">sells 100% of the copy on the trader's first sell · no take-profit or stop-loss</div>
      </div>
      <p className="mt-2 text-[10px] font-mono text-slate-500" data-testid="exit-rules-note">Traders checked every 15s · also exits if the trader no longer holds the coin</p>
      <SizeForm key={`${settings.trade_usd}-${settings.trades_per_day}`} settings={settings} refresh={refresh} />
      <div className="mt-4 divide-y divide-[#1E293B]">
        {rows.map(([k, v, c]) => (
          <div key={k} className="flex justify-between gap-4 py-2 text-xs">
            <span className="text-slate-500 shrink-0">{k}</span>
            <span className={`font-mono text-right ${c || "text-slate-200"}`}>{v}</span>
          </div>
        ))}
      </div>
      <div className={`mt-4 px-3 py-2 text-[11px] font-mono border ${settings.mode === "live" ? "border-[#EF4444]/50 text-[#EF4444] bg-[#EF4444]/10" : "border-[#6366F1]/50 text-[#a5b4fc] bg-[#6366F1]/10"}`}
        data-testid="mode-banner">
        {settings.mode === "live" ? "LIVE · real swaps on Solana mainnet" : "PAPER · real Raydium / pump.fun quotes, no funds moved"}
      </div>
    </div>
  );
};
